class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onNotificationClick = this.handleNotificationClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.notification.addEventListener('click', this.onNotificationClick);
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    window.cancelPromoPopupTimer?.();
    window.closePromoPopup?.();

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  async handleNotificationClick(event) {
    const removeButton = event.target.closest('[data-cart-notification-remove]');
    if (!removeButton || !this.contains(removeButton)) return;

    event.preventDefault();
    event.stopPropagation();
    await this.removeItem(removeButton);
  }

  async removeItem(removeButton) {
    const lineItemKey = removeButton.dataset.lineKey || this.cartItemKey;
    if (!lineItemKey || removeButton.disabled) return;

    const label = removeButton.querySelector('.cart-notification-product__remove-label');
    const errorElement = document.getElementById('cart-notification-remove-error');
    const originalLabel = label?.textContent || 'Remove';

    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    removeButton.disabled = true;
    removeButton.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Removing…';

    const sections = ['cart-notification-button', 'cart-icon-bubble'];

    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();

      if (!response.ok || state.errors) {
        throw new Error(state.description || state.message || state.errors || 'Unable to remove item');
      }

      sections.forEach((sectionId) => {
        const target = document.getElementById(sectionId);
        const html = state.sections?.[sectionId];
        if (!target || !html) return;
        target.innerHTML = this.getSectionInnerHTML(html);
      });

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-notification',
          cartData: state,
        });
      }

      this.cartItemKey = null;
      this.close();
    } catch (error) {
      console.error('Unable to remove cart item from notification:', error);

      removeButton.disabled = false;
      removeButton.removeAttribute('aria-busy');
      if (label) label.textContent = originalLabel;

      if (errorElement) {
        errorElement.textContent = 'Could not remove this item. Please try again or open your cart.';
        errorElement.hidden = false;
      }
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    const errorElement = document.getElementById('cart-notification-remove-error');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
